export type GlobalPreferences = {
  locale: "ja" | "en";
  timezone: string;
  date_format: "YYYY/MM/DD" | "YYYY-MM-DD" | "DD/MM/YYYY";
  time_format: "24h" | "12h";
  week_start: "monday" | "sunday";
  theme: "system" | "light" | "dark";
  font_scale: number;
};

const defaultPreferences: GlobalPreferences = {
  locale: "ja",
  timezone: "Asia/Tokyo",
  date_format: "YYYY/MM/DD",
  time_format: "24h",
  week_start: "monday",
  theme: "system",
  font_scale: 100,
};

let currentPreferences: GlobalPreferences = defaultPreferences;
let currentSnapshot: GlobalPreferences = currentPreferences;

export function getGlobalPreferencesSnapshot(): GlobalPreferences {
  return currentSnapshot;
}

export function applyGlobalPreferences(preferences: Partial<GlobalPreferences>) {
  currentPreferences = { ...currentPreferences, ...preferences };
  currentSnapshot = currentPreferences;
  if (typeof document === "undefined") return currentPreferences;
  const root = document.documentElement;
  const theme = currentPreferences.theme;
  const resolvedTheme =
    theme === "system"
      ? window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
        ? "dark"
        : "light"
      : theme;
  root.dataset.theme = resolvedTheme;
  root.dataset.themePreference = currentPreferences.theme;
  root.dataset.locale = currentPreferences.locale;
  root.dataset.timezone = currentPreferences.timezone;
  root.dataset.dateFormat = currentPreferences.date_format;
  root.dataset.timeFormat = currentPreferences.time_format;
  root.dataset.weekStart = currentPreferences.week_start;
  root.dataset.fontScale = String(currentPreferences.font_scale);
  root.lang = currentPreferences.locale;
  root.style.fontSize = `${currentPreferences.font_scale}%`;
  root.style.colorScheme = resolvedTheme;
  return currentPreferences;
}

export function formatGlobalDateTime(value: unknown) {
  const ts = Number(value);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const prefs = getGlobalPreferencesSnapshot();
  return `${formatDateParts(new Date(ts), prefs)} ${formatTimeParts(new Date(ts), prefs)}`.trim();
}

export function formatGlobalDate(value: unknown) {
  const ts = Number(value);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const prefs = getGlobalPreferencesSnapshot();
  return formatDateParts(new Date(ts), prefs);
}

export function formatGlobalTime(value: unknown) {
  const ts = Number(value);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  const prefs = getGlobalPreferencesSnapshot();
  return formatTimeParts(new Date(ts), prefs);
}

export function getGlobalWeekStartIndex() {
  return getGlobalPreferencesSnapshot().week_start === "sunday" ? 0 : 1;
}

export function syncGlobalPreferencesFromRecord(value: Record<string, unknown>) {
  const next = {
    locale: readLocale(value.locale),
    timezone: String(value.timezone ?? "Asia/Tokyo"),
    date_format: readDateFormat(value.date_format),
    time_format: readTimeFormat(value.time_format),
    week_start: readWeekStart(value.week_start),
    theme: readTheme(value.theme),
    font_scale: clamp(Number(value.font_scale ?? 100), 85, 130),
  } satisfies GlobalPreferences;
  currentPreferences = next;
  currentSnapshot = next;
  if (typeof document === "undefined") return;
  applyGlobalPreferences(next);
}

function formatDateParts(date: Date, prefs: GlobalPreferences) {
  const parts = partsForTimeZone(date, prefs.timezone);
  if (prefs.date_format === "YYYY-MM-DD")
    return `${parts.year}-${parts.month}-${parts.day}`;
  if (prefs.date_format === "DD/MM/YYYY")
    return `${parts.day}/${parts.month}/${parts.year}`;
  return `${parts.year}/${parts.month}/${parts.day}`;
}

function formatTimeParts(date: Date, prefs: GlobalPreferences) {
  const parts = partsForTimeZone(date, prefs.timezone);
  if (prefs.time_format === "12h") {
    const hour = Number(parts.hour);
    const suffix = hour >= 12 ? "PM" : "AM";
    const normalized = hour % 12 || 12;
    return `${String(normalized).padStart(2, "0")}:${parts.minute} ${suffix}`;
  }
  return `${parts.hour}:${parts.minute}`;
}

// Building an Intl.DateTimeFormat is expensive; the calendar renders hundreds
// of events per frame and formatted every one of them on each render.
const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function partsForTimeZone(date: Date, timezone: string) {
  let formatter = partsFormatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    partsFormatterCache.set(timezone, formatter);
  }
  const parts = formatter.formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
  };
}

function readLocale(value: unknown): "ja" | "en" {
  return String(value) === "en" ? "en" : "ja";
}

function readDateFormat(value: unknown): GlobalPreferences["date_format"] {
  const text = String(value);
  return text === "YYYY-MM-DD" || text === "DD/MM/YYYY" ? text : "YYYY/MM/DD";
}

function readTimeFormat(value: unknown): GlobalPreferences["time_format"] {
  return String(value) === "12h" ? "12h" : "24h";
}

function readWeekStart(value: unknown): GlobalPreferences["week_start"] {
  return String(value) === "sunday" ? "sunday" : "monday";
}

function readTheme(value: unknown): GlobalPreferences["theme"] {
  const text = String(value);
  return text === "light" || text === "dark" ? text : "system";
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}
